import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import { ArrowRight, Calendar, FileCode, TrendingUp, Settings, Eye } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const WFR_BURDENS = [
  {
    icon: Settings,
    title: 'Per-supplier template maintenance',
    detail:
      'Every supplier with a non-trivial invoice layout needs its own WFR template. New suppliers need new templates. Supplier layout changes break existing templates. Template maintenance is an ongoing operational cost that scales with supplier diversity.',
  },
  {
    icon: Eye,
    title: 'The verifier queue',
    detail:
      'Low-confidence extractions route to a human verifier. The verifier queue becomes a load-bearing AP team activity — often consuming 1-3 FTE in larger AP operations. Throughput is gated by verifier capacity.',
  },
  {
    icon: TrendingUp,
    title: 'Accuracy degrades on unseen invoices',
    detail:
      'WFR\'s template-based recognition works well on suppliers it has been trained on. New suppliers, unusual layouts, and structural variations send extractions to the verifier queue or fail outright. Accuracy is bounded by template coverage.',
  },
  {
    icon: FileCode,
    title: 'Skills concentration risk',
    detail:
      'WFR template authoring is a niche skill. Most AP teams rely on a small number of specialists — often a single individual — to maintain the template library. Departure risk is real and concentrated.',
  },
];

const COMPARISON = [
  {
    capability: 'Recognition approach',
    wfr: 'Per-supplier templates trained on prior invoices',
    modern: 'AI extraction that reads unseen invoices without prior training',
  },
  {
    capability: 'Maintenance burden',
    wfr: 'New supplier = new template; layout change = template rework',
    modern: 'AI learns from corrections; no per-supplier template authoring',
  },
  {
    capability: 'Confidence handling',
    wfr: 'Verifier queue with manual review of low-confidence fields',
    modern: 'Per-field confidence scoring with configurable thresholds; routing only for fields below threshold',
  },
  {
    capability: 'New supplier extraction',
    wfr: 'Often requires template authoring before clean extraction',
    modern: 'Day-one extraction without per-supplier setup; improves with corrections',
  },
  {
    capability: 'Layout adaptability',
    wfr: 'Brittle to layout changes — templates need rework',
    modern: 'Resilient to layout variation; learns from each successful extraction',
  },
  {
    capability: 'Skill requirement',
    wfr: 'WFR template authoring specialists',
    modern: 'Configuration-driven; no specialist authoring required',
  },
];

// The three realistic forward options for a WFR implementation at the 12c
// boundary. Deliberately phrased as "replacement options / alternatives" —
// that is the buyer's actual search language and no one else answers it.
const REPLACEMENT_OPTIONS = [
  {
    option: 'Option 1 — Upgrade WFR within WebCenter 14c',
    what: 'Stay on WFR. Upgrade the WebCenter stack from 12c to 14c (14.1.2) and carry the template library forward.',
    fits: 'Deep template libraries that work well today, teams with WFR authoring skills in-house, and stacks upgrading to 14c anyway.',
    consider: 'Template maintenance and the verifier queue continue as-is. Templates need format validation during the upgrade. The same lifecycle decision recurs at the 14c support boundary.',
    link: '/solutions/webcenter-14c-upgrade',
    linkLabel: 'WebCenter 14c upgrade path',
  },
  {
    option: 'Option 2 — Replace WFR with modern AI extraction',
    what: 'Retire per-supplier templates. AI extraction reads unseen invoices without prior training, with per-field confidence scoring and learning from corrections.',
    fits: 'High supplier diversity, template-maintenance fatigue, verifier-queue bottlenecks, or WFR skills concentrated in one or two people.',
    consider: 'Templates themselves don\'t transfer — but the business knowledge they encode does, via a WFR Rules Migration Audit. Requires an implementation project (typically months, not weeks).',
    link: '/solutions/wfr-vs-oci-document-understanding',
    linkLabel: 'WFR vs OCI Document Understanding comparison',
  },
  {
    option: 'Option 3 — Remain on 12c under Sustaining Support',
    what: 'Make no change. After Extended Support ends December 2027, WFR 12c continues under Sustaining Support — existing patches and documentation, but no new patches, security updates, or certifications.',
    fits: 'Implementations already scheduled for decommission, or as a deliberate short bridge while Option 1 or 2 is executed.',
    consider: 'Security and certification exposure grows over time, and the decision is deferred rather than made. See the Sustaining Support decision guide before choosing this path.',
    link: '/solutions/webcenter-sustaining-support-decision',
    linkLabel: 'Sustaining Support decision guide',
  },
];

const FAQS = [
  {
    q: 'What is Oracle WebCenter Forms Recognition (WFR)?',
    a: 'Oracle WebCenter Forms Recognition (WFR) is the data-extraction layer in the Oracle WebCenter AP stack. It uses per-supplier templates to recognize invoice fields — vendor name, invoice number, line items, totals, taxes — with a verifier queue for fields below confidence threshold. WFR is the standard extraction engine in FIPSA implementations and many custom WebCenter AP deployments.',
  },
  {
    q: 'Why modernize WFR?',
    a: 'Three reasons. First, the Fusion Middleware 12c support cliff — WFR is part of FMW 12c, with Premier Support ending December 2026. Second, the template maintenance burden — per-supplier templates require ongoing authoring as suppliers and layouts change. Third, the verifier queue — low-confidence extractions create a load-bearing AP team activity that throttles throughput. Modern AI extraction eliminates the template authoring requirement and reduces verifier work to a small fraction of invoices.',
  },
  {
    q: 'What replaces WFR in a modernization?',
    a: 'AI extraction with confidence scoring per field. The model reads unseen invoices without prior per-supplier training, scores each extracted field for confidence, and routes only fields below threshold for human review. The model learns from corrections — when an AP staffer corrects an extraction, the model improves on the next similar invoice without explicit template authoring.',
  },
  {
    q: 'Can we keep our existing WFR templates after modernization?',
    a: 'The templates themselves don\'t transfer (different recognition architecture), but the business knowledge they encode does — supplier patterns, field locations, expected formats. A WFR Rules Migration Audit (one of EZ Cloud\'s engagement tiers) reads your existing WFR template library and translates the business knowledge into the modern system as initial training context.',
  },
  {
    q: 'What are the replacement options for Oracle WebCenter Forms Recognition?',
    a: 'There are three realistic forward options at the Fusion Middleware 12c boundary. Option 1: stay on WFR and upgrade the WebCenter stack to 14c (14.1.2) — the template library carries forward with format validation. Option 2: replace WFR with modern AI extraction — per-supplier templates are retired in favor of a model that reads unseen invoices, scores each field for confidence, and learns from corrections. Option 3: remain on 12c under Sustaining Support — no new patches or certifications after December 2027, so this fits as a short bridge or pre-decommission state rather than a destination. The right option depends on template library depth, supplier diversity, verifier-queue load, and in-house WFR skills.',
  },
  {
    q: 'Is there a direct Oracle successor or alternative to WFR?',
    a: 'Within the Oracle stack, WFR itself continues in WebCenter 14c — upgrading is the "stay on WFR" path. Oracle also offers OCI Document Understanding, an OCI-native AI document-extraction service, which takes a fundamentally different (model-based, not template-based) approach; see our dedicated WFR vs OCI Document Understanding comparison. Outside the Oracle extraction stack, modern AI extraction platforms (including EZ Cloud) replace the template-plus-verifier architecture entirely while keeping Oracle EBS or Fusion as the system of record.',
  },
  {
    q: 'How accurate is AI extraction compared to WFR?',
    a: 'Day-one extraction rates land at 88-90% across PO and non-PO invoices in typical implementations, climbing as the model learns from corrections. WFR accuracy on covered suppliers is also high — the difference isn\'t accuracy on trained suppliers; it\'s accuracy on suppliers WFR hasn\'t been trained on, where AI extraction reads the invoice anyway and WFR routes to the verifier queue.',
  },
  {
    q: 'What about non-PO coding?',
    a: 'Non-PO coding in WebCenter AP is typically handled by the Oracle ADF coding form — notoriously development-heavy to customize. In a modernization, the ADF form is replaced by a configurable web UI that AP staff can adjust without ADF development cycles. The same business logic — approval routing, GL coding rules, hold codes — is preserved.',
  },
];

const ModernizeWebCenterFormsRecognition = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Oracle WebCenter Forms Recognition (WFR) — Replacement Options & Modernization',
    description:
      'The three WFR replacement options at the FMW 12c boundary: upgrade WFR within WebCenter 14c, replace per-supplier templates with AI extraction, or bridge on Sustaining Support. How EZ Cloud reads your existing WFR template library and modernizes the extraction layer.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-07-07',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/modernize-webcenter-forms-recognition',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter Forms Recognition' },
      { '@type': 'Thing', name: 'WFR replacement options' },
      { '@type': 'Thing', name: 'WFR alternatives' },
      { '@type': 'Thing', name: 'AI invoice extraction' },
      { '@type': 'Thing', name: 'Oracle WebCenter AP modernization' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter Forms Recognition (WFR) — Replacement Options & Modernization | EZ Cloud"
        description="The three WFR replacement options at the FMW 12c boundary: upgrade WFR within WebCenter 14c, replace per-supplier templates with AI extraction, or bridge on Sustaining Support. How EZ Cloud reads your existing WFR template library and modernizes the extraction layer."
        canonical="https://ezcloud.ai/solutions/modernize-webcenter-forms-recognition"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'Modernize WFR',
            url: 'https://ezcloud.ai/solutions/modernize-webcenter-forms-recognition',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-07-07" />

      <main>
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <FileCode className="w-4 h-4" aria-hidden />
              WFR Modernization
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Modernize Oracle WebCenter Forms Recognition.
              <span className="block text-accent mt-3">
                Eliminate the template maintenance burden.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              WebCenter Forms Recognition (WFR) is the data-extraction layer in the WebCenter AP
              stack — per-supplier templates with a verifier queue. EZ Cloud replaces WFR with AI
              extraction that reads unseen invoices and learns from corrections, without per-supplier
              template authoring.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-page')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WFR Modernization Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What WFR is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Oracle WebCenter Forms Recognition, defined
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle WebCenter Forms Recognition (WFR) is the data-extraction layer in the
                  Oracle WebCenter AP stack.
                </strong>{' '}
                It uses per-supplier templates to recognize invoice fields — vendor name, invoice
                number, line items, totals, taxes — with a verifier queue for fields below
                confidence threshold. WFR is the standard extraction engine in FIPSA implementations
                and many custom WebCenter AP deployments built between 2010 and 2024.
              </p>
              <p>
                Modernizing WFR replaces the per-supplier template model with AI extraction that
                reads unseen invoices without prior training, scores each field for confidence,
                routes only low-confidence fields for human review, and learns from corrections.
                The result is the same business outcome — accurate, structured invoice data flowing
                into Oracle Payables — with materially less ongoing maintenance.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The four WFR burdens we hear about most
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              Why customers modernize WFR
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {WFR_BURDENS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              WFR vs. modern AI extraction
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Capability mapping
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              How each WFR capability translates forward in a modernized AP automation platform.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/4">
                      Capability
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-3/8">
                      WFR today
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-3/8">
                      EZ Cloud modernized
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {COMPARISON.map((row) => (
                    <tr key={row.capability} className="border-b border-gray-200 last:border-b-0">
                      <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                        {row.capability}
                      </td>
                      <td className="px-5 py-4 text-gray-700">{row.wfr}</td>
                      <td className="px-5 py-4 text-gray-700">
                        <strong className="font-semibold text-gray-900">{row.modern}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* WFR replacement options — targets "wfr replacement / alternatives" intent */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Replacement options
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The three WFR replacement options at the 12c boundary
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Every WFR implementation faces the same December 2026 / 2027 window as the rest of
              Fusion Middleware 12c. There are three realistic forward options — all legitimate,
              each with a different fit.
            </p>
            <div className="space-y-6">
              {REPLACEMENT_OPTIONS.map((o) => (
                <Card key={o.option} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{o.option}</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">{o.what}</p>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm mb-4">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-bold uppercase tracking-wide text-accent mb-1">
                        Fits when
                      </p>
                      <p className="text-gray-700 leading-relaxed">{o.fits}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-bold uppercase tracking-wide text-accent mb-1">
                        Consider
                      </p>
                      <p className="text-gray-700 leading-relaxed">{o.consider}</p>
                    </div>
                  </div>
                  <Link
                    to={o.link}
                    className="inline-flex items-center text-sm font-semibold text-primary hover:text-accent transition-colors"
                  >
                    {o.linkLabel}
                    <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
                  </Link>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 md:p-10 bg-accent/5 border border-accent/30 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wide text-accent mb-3">
                The WFR Rules Migration Audit
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                A fixed-fee read of your existing WFR template library
              </h3>
              <p className="text-gray-700 leading-relaxed mb-5">
                One of EZ Cloud's productized engagement tiers. Andrew reads your existing WFR
                template library directly, inventories the supplier coverage, identifies where the
                business knowledge lives, and produces a migration plan that translates the WFR
                business knowledge into a modern AI extraction model. The deliverable is yours to
                keep whether you proceed to implementation with EZ Cloud or not.
              </p>
              <Link
                to="/solutions/modernize-oracle-webcenter"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See all engagement tiers and the full modernization path
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </Card>
          </div>
        </section>

        {/* Decision Guide CTA */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths, stack-specific considerations
              (including WFR), eight diagnostic questions, and vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on WFR modernization
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Let's read your WFR templates directly
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute call with {FOUNDER.name}, founder of EZ Cloud and a 25-year Oracle
              WebCenter AP specialist. We'll talk through your WFR footprint and what modernization
              looks like for your specific template library.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-page')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a WFR Modernization Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ModernizeWebCenterFormsRecognition;
